/**
 * Iter 67ei — Public affiliate leaderboard.
 *
 * Lives at `/widget-builder/top-partners`. Renders opt-in affiliate
 * profiles (claimed via the builder) sorted by confirmed bookings in the
 * trailing window. Pure social-proof page — no auth needed.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { Trophy, ExternalLink, ArrowLeft, Sparkles } from "lucide-react";

function _initials(name) {
  return (name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0]?.toUpperCase())
    .slice(0, 2)
    .join("");
}

export default function AffiliateLeaderboardPage() {
  const [partners, setPartners] = useState([]);
  const [windowDays, setWindowDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/public/affiliate-leaderboard", { params: { days: windowDays, limit: 50 } })
      .then(({ data }) => {
        if (!cancelled) setPartners(data.partners || []);
      })
      .catch(() => { if (!cancelled) setPartners([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [windowDays]);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 max-w-5xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link
          to="/widget-builder"
          className="inline-flex items-center gap-1.5 text-sm text-neutral-600 hover:text-amber-700 mb-6"
          data-testid="leaderboard-back-link"
        >
          <ArrowLeft className="h-4 w-4" /> Back to widget builder
        </Link>

        <div className="mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 text-amber-900 text-[11px] font-bold uppercase tracking-[0.18em] mb-4">
            <Trophy className="h-3 w-3" /> Top partners
          </div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tighter font-semibold leading-[1.05]" data-testid="leaderboard-title">
            Travel sites driving the<br />
            <span className="text-amber-600">most bookings</span>
          </h1>
          <p className="mt-4 text-neutral-600 max-w-2xl">
            Real partners running the RentalCar.al search widget on their own
            sites. Rankings are based on confirmed bookings in the trailing
            window. Want to be here?{" "}
            <Link to="/widget-builder" className="text-amber-700 font-semibold underline" data-testid="leaderboard-cta-build">
              Build your widget →
            </Link>
          </p>
        </div>

        {/* Window selector */}
        <div className="flex items-center gap-2 mb-6" data-testid="leaderboard-window-chips">
          <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-neutral-500 mr-1">
            <Sparkles className="inline h-3 w-3 mr-1" /> Window
          </span>
          {[7, 30, 90, 365].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setWindowDays(d)}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition ${windowDays === d ? "bg-amber-500 text-amber-950" : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"}`}
              data-testid={`leaderboard-window-${d}`}
            >
              {d === 365 ? "1 year" : `${d}d`}
            </button>
          ))}
        </div>

        {/* Partners list */}
        {loading ? (
          <div className="rounded-2xl border border-border bg-white p-8 text-center text-neutral-500" data-testid="leaderboard-loading">
            Loading leaderboard…
          </div>
        ) : partners.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-amber-300 bg-amber-50/40 p-8 text-center" data-testid="leaderboard-empty">
            <Trophy className="h-8 w-8 mx-auto text-amber-500 mb-3" />
            <div className="font-display text-xl font-semibold mb-1">No public partners yet</div>
            <div className="text-sm text-neutral-600 max-w-md mx-auto">
              You could be the first one listed here. Build your widget, claim
              your affiliate ID, opt-in to the public leaderboard, and start
              earning free brand exposure on RentalCar.al.
            </div>
            <Link
              to="/widget-builder"
              className="inline-flex items-center gap-2 px-5 py-2.5 mt-5 rounded-full bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold text-sm transition"
              data-testid="leaderboard-empty-cta"
            >
              Get started
            </Link>
          </div>
        ) : (
          <div className="space-y-2" data-testid="leaderboard-list">
            {partners.map((p, i) => (
              <PartnerRow key={p.affiliate_id} partner={p} rank={i + 1} />
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="mt-12 rounded-3xl bg-gradient-to-br from-amber-100 via-amber-50 to-white border border-amber-200 p-8 text-center">
          <div className="font-display text-2xl sm:text-3xl font-semibold mb-2">
            Want your site listed here?
          </div>
          <p className="text-neutral-700 max-w-xl mx-auto text-sm sm:text-base mb-5">
            Drop the search widget on your site, claim your affiliate ID, and
            tick "show on public leaderboard" — we'll feature your brand here
            as soon as your first booking lands.
          </p>
          <Link
            to="/widget-builder"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold text-sm transition shadow-lg shadow-amber-500/20"
            data-testid="leaderboard-bottom-cta"
          >
            Build your widget
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function PartnerRow({ partner, rank }) {
  const medal = rank === 1 ? "🥇" : rank === 2 ? "🥈" : rank === 3 ? "🥉" : null;
  return (
    <div
      className="rounded-2xl border border-border bg-white p-4 sm:p-5 flex items-center gap-4 hover:shadow-md transition"
      data-testid={`leaderboard-row-${partner.affiliate_id}`}
    >
      <div className={`w-10 text-center font-display text-2xl font-semibold ${rank <= 3 ? "" : "text-neutral-400"}`} data-testid="leaderboard-row-rank">
        {medal || `#${rank}`}
      </div>

      {partner.logo_url ? (
        <img
          src={partner.logo_url}
          alt={partner.display_name}
          className="h-12 w-12 rounded-xl object-contain bg-neutral-50 p-1.5 shrink-0 ring-1 ring-neutral-200"
          onError={(e) => { e.target.style.display = "none"; }}
        />
      ) : (
        <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-amber-300 to-amber-500 flex items-center justify-center text-white font-display font-semibold shrink-0">
          {_initials(partner.display_name)}
        </div>
      )}

      <div className="min-w-0 flex-1">
        <div className="font-medium text-sm sm:text-base truncate" data-testid="leaderboard-row-name">
          {partner.display_name}
        </div>
        {partner.tagline && (
          <div className="text-xs text-neutral-500 truncate mt-0.5">{partner.tagline}</div>
        )}
        {partner.website && (
          <a
            href={partner.website}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[11px] text-amber-700 hover:underline inline-flex items-center gap-1 mt-1"
            data-testid="leaderboard-row-website"
          >
            {(() => {
              try { return new URL(partner.website).hostname; }
              catch { return partner.website; }
            })()}
            <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>

      <div className="text-right shrink-0">
        <div className="font-display text-2xl sm:text-3xl font-semibold text-amber-600 tabular-nums" data-testid="leaderboard-row-bookings">
          {partner.confirmed_bookings}
        </div>
        <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">
          confirmed booking{partner.confirmed_bookings === 1 ? "" : "s"}
        </div>
      </div>
    </div>
  );
}
